import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { readerFor, useLocalizedContent } from '../../i18n/localized.ts';
import type { TFunction } from 'i18next';
import { templateApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import {
  Button,
  Checkbox,
  classes,
  ConfirmDialog,
  EmptyState,
  LoadingBlock,
  Select,
  TextArea,
  TextInput,
} from '../ui.tsx';
import { segmentsForQuote, TemplatePicker } from '../template-picker.tsx';
import { QuantityStepper } from './quantity-stepper.tsx';
import { RoomTasks } from './room-tasks.tsx';
import { CommercialPresets } from './commercial-presets.tsx';
import { FirstVisitTasksToggle } from './first-clean-card.tsx';
import { isPersistedId, temporaryId } from '../../hooks/quote-merge.ts';
import { useListMotion } from '../../hooks/list-motion.ts';
import {
  conditionMultiplierFor,
  FLOOR_TYPES,
  MAX_GROUP_QUANTITY,
  MAX_LAYOUT_ROOMS,
  MAX_MEASURED_SQUARE_FEET,
  MAX_QUOTE_CUSTOMER_NOTES_LENGTH,
  MAX_QUOTE_ROOMS,
  MAX_ROOM_QUANTITY,
  ROOM_CONDITIONS,
  ROOM_SIZES,
  sizeMultiplierFor,
} from '@shared/constants/index.ts';
import { ROOM_CATEGORIES } from '@shared/constants/template-taxonomy.ts';
import { customerNotesForSavedGroup } from '@shared/constants/seed-group-templates.ts';
import { clampGroupQuantity, defaultGroupOf, groupsChromeVisible } from '@shared/quotes/groups.ts';
import {
  calculateRoom,
  calculateRoomChargeCents,
  formatCents,
  groupSubtotals,
  measuredAreaEffect,
} from '@shared/calculations/pricing.ts';
import { calculationInputFor } from '../../hooks/use-quote-editor.ts';
import { formatMinutes } from '../../i18n/format.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import type {
  GroupTemplate,
  GroupTemplateRoom,
  Quote,
  QuoteGroup,
  QuoteRoom,
  RoomTemplate,
  ServiceTemplate,
} from '@shared/types/index.ts';

/**
 * Step 3 (spec §16.1): add, duplicate, rename, reorder and remove rooms, and
 * set each room's size and condition.
 */

/**
 * Builds a room's task list from a template.
 *
 * The values are copied here and travel with the quote from then on — editing
 * the template later never rewrites an existing quote (spec §8.11).
 */
export function roomFromTemplate(
  template: RoomTemplate,
  services: ServiceTemplate[],
  /**
   * Whether the room type's flat price can be copied. It is an amount in the
   * company currency, so not onto a quote written in another one (a location
   * in its own currency): the room is priced from its tasks there instead.
   */
  flatPriceApplies = true,
): QuoteRoom {
  const byId = new Map(services.map((service) => [service.id, service]));

  return {
    id: temporaryId(),
    roomTemplateId: template.id,
    roomName: template.name,
    // The room type's other languages travel with its name, so the crew sheet
    // reads in the cleaner's language while the quote reads in the customer's
    // (see localized-text.ts). Snapshotted like everything else here: retranslating the room
    // type later must not rewrite a quote already sent.
    roomNameTranslations: template.nameTranslations,
    quantity: Math.min(MAX_ROOM_QUANTITY, Math.max(1, Math.trunc(template.defaultQuantity ?? 1) || 1)),
    sizeCode: 'medium',
    sizeMultiplierBasisPoints: 10_000,
    conditionCode: 'average',
    conditionMultiplierBasisPoints: 10_000,
    baseMinutes: 0,
    calculatedMinutes: 0,
    adjustedMinutes: null,
    billableMinutes: 0,
    isManuallyAdjusted: false,
    // Copied from the room type, and the quote's from then on: re-pricing the
    // room type later must not re-price a quote already sent (spec §8.11).
    flatPriceCents: flatPriceApplies ? template.defaultFlatPriceCents : null,
    chargeCents: null,
    // The room type says whether this kind of area is measured and what is
    // usually underfoot; the square footage itself comes from the walkthrough,
    // so it starts unmeasured rather than guessed.
    measuredSquareFeet: template.defaultSquareFeet ?? null,
    floorType: template.measurementBasis === 'time' ? null : template.defaultFloorType,
    customerNotes: null,
    internalNotes: null,
    displayOrder: 0,
    services: template.services
      .map((link, index) => {
        const service = byId.get(link.serviceTemplateId);
        if (!service) return null;
        return {
          id: temporaryId(),
          serviceTemplateId: service.id,
          name: service.name,
          description: service.customerDescription,
          nameTranslations: service.nameTranslations,
          descriptionTranslations: service.customerDescriptionTranslations,
          estimatedMinutes: link.defaultMinutesOverride ?? service.defaultMinutes,
          isCustom: false,
          isSelected: link.defaultSelected,
          showOnCustomerQuote: service.showOnCustomerQuote,
          isTaxable: service.isTaxable,
          frequency: 'every_visit' as const,
          // The library's measurement travels with the task; the quantity does
          // not, because nobody has measured this building yet.
          measurementBasis: service.measurementBasis,
          measuredQuantity: link.defaultMeasuredQuantity ?? null,
          productionRatePerHour: service.measurementBasis === 'time' ? null : service.productionRatePerHour,
          customerNotes: null,
          internalNotes: null,
          displayOrder: index,
        };
      })
      .filter((service): service is NonNullable<typeof service> => service !== null),
  };
}

export function blankRoom(name: string): QuoteRoom {
  return {
    id: temporaryId(),
    roomTemplateId: null,
    roomName: name,
    // Somebody typed one string, so there is one string. Inventing the other
    // languages would put a machine guess on a customer's quote (see localized-text.ts).
    roomNameTranslations: null,
    quantity: 1,
    sizeCode: 'medium',
    sizeMultiplierBasisPoints: 10_000,
    conditionCode: 'average',
    conditionMultiplierBasisPoints: 10_000,
    baseMinutes: 0,
    calculatedMinutes: 0,
    adjustedMinutes: null,
    billableMinutes: 0,
    isManuallyAdjusted: false,
    // A custom area has no room type to inherit a price from, so it is priced
    // by time until someone types a price on it.
    flatPriceCents: null,
    chargeCents: null,
    // A custom area has no room type to inherit a measurement from either.
    measuredSquareFeet: null,
    floorType: null,
    customerNotes: null,
    internalNotes: null,
    displayOrder: 0,
    services: [],
  };
}

/**
 * The groups a quote is edited as. A draft that has not loaded any yet is one
 * untouched default group — the same shape the server stores for a residential
 * quote — so "Add group" has something to sit beside.
 */
function quoteGroups(quote: Quote): QuoteGroup[] {
  if (quote.groups && quote.groups.length > 0) {
    return [...quote.groups].sort(
      (left, right) => left.displayOrder - right.displayOrder || left.id.localeCompare(right.id),
    );
  }
  return [
    {
      id: '',
      name: '',
      nameTranslations: null,
      quantity: 1,
      notes: null,
      isDefault: true,
      displayOrder: 0,
    },
  ];
}

function roomGroupId(room: QuoteRoom, groups: readonly QuoteGroup[]): string {
  if (room.groupId && groups.some((group) => group.id === room.groupId)) return room.groupId;
  return defaultGroupOf(groups)?.id ?? '';
}

function attachOrphans(rooms: QuoteRoom[], groups: readonly QuoteGroup[]): QuoteRoom[] {
  return rooms.map((room) => ({ ...room, groupId: roomGroupId(room, groups) }));
}

function insertInGroup(rooms: QuoteRoom[], room: QuoteRoom): QuoteRoom[] {
  if (!room.groupId) return [...rooms, room];
  let last = -1;
  rooms.forEach((candidate, index) => {
    if (candidate.groupId === room.groupId) last = index;
  });
  if (last < 0) return [...rooms, room];
  return [...rooms.slice(0, last + 1), room, ...rooms.slice(last + 1)];
}

function serviceIdForSnapshot(
  service: GroupTemplateRoom['services'][number],
  templates: readonly { id: string; seedKey?: string | null }[],
): string | null {
  if (service.serviceTemplateId && templates.some((item) => item.id === service.serviceTemplateId)) {
    return service.serviceTemplateId;
  }
  if (service.serviceKey) {
    const match = templates.find((item) => item.seedKey && item.seedKey === service.serviceKey);
    if (match) return match.id;
  }
  return service.serviceTemplateId;
}

function roomFromGroupSnapshot(
  room: GroupTemplateRoom,
  groupId: string,
  templates: readonly { id: string; seedKey?: string | null }[],
  roomTemplates: readonly { id: string; seedKey?: string | null }[],
  /** As in `roomFromTemplate`: a layout's flat prices are company-currency amounts. */
  flatPriceApplies = true,
): QuoteRoom {
  const linkedRoomId =
    room.roomTemplateId && roomTemplates.some((item) => item.id === room.roomTemplateId) ? room.roomTemplateId : null;
  const roomTemplateId =
    linkedRoomId ??
    (room.roomKey ? (roomTemplates.find((item) => item.seedKey && item.seedKey === room.roomKey)?.id ?? null) : null);
  return {
    id: temporaryId(),
    roomTemplateId,
    roomName: room.roomName,
    roomNameTranslations: room.roomNameTranslations,
    quantity: Math.min(MAX_ROOM_QUANTITY, Math.max(1, Math.trunc(room.quantity) || 1)),
    sizeCode: room.sizeCode,
    sizeMultiplierBasisPoints: sizeMultiplierFor(room.sizeCode),
    conditionCode: room.conditionCode,
    conditionMultiplierBasisPoints: conditionMultiplierFor(room.conditionCode),
    baseMinutes: 0,
    calculatedMinutes: 0,
    adjustedMinutes: room.adjustedMinutes ?? null,
    billableMinutes: 0,
    isManuallyAdjusted: room.adjustedMinutes != null,
    flatPriceCents: flatPriceApplies ? room.flatPriceCents : null,
    chargeCents: null,
    measuredSquareFeet: room.measuredSquareFeet,
    floorType: room.floorType,
    customerNotes: room.customerNotes,
    internalNotes: null,
    displayOrder: 0,
    groupId,
    services: room.services.map((service, index) => {
      const serviceTemplateId = serviceIdForSnapshot(service, templates);
      return {
        id: temporaryId(),
        serviceTemplateId,
        name: service.name,
        description: service.description,
        nameTranslations: service.nameTranslations,
        descriptionTranslations: service.descriptionTranslations,
        estimatedMinutes: service.estimatedMinutes,
        isCustom: serviceTemplateId === null,
        isSelected: service.isSelected,
        showOnCustomerQuote: service.showOnCustomerQuote,
        isTaxable: service.isTaxable,
        frequency: service.frequency,
        measurementBasis: service.measurementBasis,
        measuredQuantity: service.measuredQuantity,
        productionRatePerHour: service.productionRatePerHour,
        customerNotes: null,
        internalNotes: null,
        displayOrder: index,
      };
    }),
  };
}

/**
 * A quote room as a layout room: the same snapshot the Settings editor
 * stores, so a layout saved from a quote inserts the rooms, tasks and typed
 * times the estimator had, not the library's defaults.
 */
function layoutRoomFromQuoteRoom(
  room: QuoteRoom,
  /**
   * Whether the quote's flat prices are in the company currency. A layout is
   * shared by every location, so a price from a quote in another currency is
   * left off rather than saved as a number every other quote would misread.
   */
  flatPriceApplies = true,
): GroupTemplateRoom {
  return {
    roomTemplateId: room.roomTemplateId,
    roomName: room.roomName,
    roomNameTranslations: room.roomNameTranslations,
    quantity: Math.min(MAX_ROOM_QUANTITY, Math.max(1, Math.trunc(room.quantity) || 1)),
    sizeCode: room.sizeCode,
    conditionCode: room.conditionCode,
    flatPriceCents: flatPriceApplies ? room.flatPriceCents : null,
    measuredSquareFeet: room.measuredSquareFeet,
    floorType: room.floorType,
    customerNotes: room.customerNotes,
    adjustedMinutes: room.isManuallyAdjusted ? room.adjustedMinutes : null,
    services: room.services.map((service) => ({
      serviceTemplateId: service.serviceTemplateId,
      name: service.name,
      description: service.description,
      nameTranslations: service.nameTranslations,
      descriptionTranslations: service.descriptionTranslations,
      estimatedMinutes: service.estimatedMinutes,
      isSelected: service.isSelected,
      showOnCustomerQuote: service.showOnCustomerQuote,
      isTaxable: service.isTaxable,
      frequency: service.frequency,
      measurementBasis: service.measurementBasis,
      measuredQuantity: service.measuredQuantity,
      productionRatePerHour: service.productionRatePerHour,
    })),
  };
}

/** How many rooms a card counts: its quantity, never below one. */
function roomQuantity(room: QuoteRoom): number {
  return Math.max(1, Math.trunc(room.quantity) || 1);
}

function cardMotionKey(roomId: string): string {
  return `card:${roomId}`;
}

/**
 * What to say under the square-feet box, given what the area does with it.
 *
 * The ordinary hint explains the field; the other two correct an impression
 * the field gives on its own. `measuredAreaEffect` is the authority on which
 * applies — it lives beside the pricing rules so this can never go on
 * promising something the engine stopped doing.
 */
function squareFeetHint(room: QuoteRoom, t: TFunction): string {
  // Nothing typed yet: nothing to be wrong about.
  if (room.measuredSquareFeet === null || room.measuredSquareFeet === undefined) {
    return t('roomsStep.squareFeetHint');
  }
  switch (measuredAreaEffect(room)) {
    case 'unused':
      return t('roomsStep.squareFeetUnusedHint');
    case 'overridden_on_tasks':
      return t('roomsStep.squareFeetTaskQuantityHint');
    case 'task_not_selected':
      return t('roomsStep.squareFeetTaskOffHint');
    case 'pinned_by_override':
      return t('roomsStep.squareFeetAdjustedHint');
    case 'drives_time_only':
      return t('roomsStep.squareFeetFlatPriceHint');
    default:
      return t('roomsStep.squareFeetHint');
  }
}

interface RoomsStepProps {
  quote: Quote;
  update: (mutate: (draft: Quote) => Quote) => void;
  readOnly: boolean;
  onOpenTasks: (roomId: string) => void;
  /** Whether "Save these rooms as a layout" is offered: a layout is a company setting. */
  canSaveLayout?: boolean;
  /**
   * The company currency, which the room library's flat prices are in. On a
   * quote in another currency they are not copied (see `roomFromTemplate`).
   */
  companyCurrencyCode?: string;
}

export default function RoomsStep({
  quote,
  update,
  readOnly,
  onOpenTasks,
  canSaveLayout = false,
  companyCurrencyCode,
}: RoomsStepProps) {
  const { t } = useTranslation();
  // The room library's and the layouts' flat prices are company-currency
  // amounts: copied in, or saved out, only on a quote in that currency.
  const flatPricesApply = quote.currencyCode === (companyCurrencyCode ?? quote.currencyCode);
  const screenLanguage = useLocalizedContent().language;
  const queryClient = useQueryClient();
  /*
    Room names here follow the QUOTE's language, not the reader's.

    This step authors a customer document: the name in the box is the one the
    quote page and the PDF will print, and the box is editable. Showing the
    estimator's own language instead would let a Spanish-reading estimator
    retype a name onto an English quote having never been shown the English one
    they were replacing — and since a hand-typed name drops the map, the
    customer's quote would come out in a language nobody chose.

    The crew's side of this is the work order, which does follow its reader.
  */
  const localized = readerFor(quote.language);
  const roomLabel = (room: QuoteRoom) => localized.text(room.roomName, room.roomNameTranslations);
  const [customName, setCustomName] = useState('');
  const [addCount, setAddCount] = useState(1);
  const [undo, setUndo] = useState<{ room: QuoteRoom; index: number } | null>(null);
  // Whether the room picker is open under a quote that already has rooms.
  // An empty quote opens on the picker and keeps it open as the first rooms
  // go in, rather than folding it away after the first one.
  const [addOpen, setAddOpen] = useState(quote.rooms.length === 0);
  const listFirst = quote.rooms.length > 0;
  useEffect(() => {
    if (!listFirst) setAddOpen(true);
  }, [listFirst]);
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  // Which room's task list to bring on screen. A token, not just the id:
  // the room may already be open, and clicking "Choose tasks" for it has
  // to scroll and focus anyway.
  const [tasksFocus, setTasksFocus] = useState<{ roomId: string; token: number } | null>(null);
  // The card "List one separately" just made, whose name box takes focus.
  const [nameFocus, setNameFocus] = useState<string | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  // Units stay off until someone asks for them, including on a commercial
  // quote. Naming a unit, counting it, or adding a second one keeps them on
  // after a reload. This flag only covers the moment before that first edit.
  const [unitsOn, setUnitsOn] = useState(false);
  // A layout picked while the quote already has rooms waits here for the
  // estimator to say whether it replaces them or joins them.
  const [layoutPending, setLayoutPending] = useState<GroupTemplate | null>(null);
  const [layoutMode, setLayoutMode] = useState<'replace' | 'add'>('replace');
  const [everyLayout, setEveryLayout] = useState(false);
  const [layoutDraft, setLayoutDraft] = useState<{ name: string; busy: boolean; error: string | null } | null>(null);
  // A unit waiting for "Remove this unit" to be confirmed. Removing one drops
  // its name, quantity and notes and folds its rooms into another unit, which
  // changes the price; unlike a room, there is no Undo for it.
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  // The unit that takes focus after another unit is removed (the Add heading
  // once units fold away): the button that asked is gone with its unit.
  const [unitFocus, setUnitFocus] = useState<string | null>(null);
  const [layoutStatus, setLayoutStatus] = useState<string | null>(null);
  const groups = quoteGroups(quote);
  const chrome = groupsChromeVisible(groups);
  const showGroups = chrome || unitsOn;
  const fallbackGroup = defaultGroupOf(groups);
  const activeGroup = groups.find((group) => group.id === activeGroupId) ?? fallbackGroup ?? groups[0] ?? null;
  /**
   * Autosave replaces a temporary group id with the server's ULID in place.
   * The selected id has to follow that swap: leaving it on the temporary id
   * makes the lookup miss, and the next room lands in the default group and
   * is priced once instead of by the group quantity just entered.
   *
   * The merge keeps group order, so the id now at the selected group's last
   * index is the same group. Removal sets a fallback id itself, before this
   * runs, and that id is still in the list. A unit waiting on "Remove this
   * unit" is carried the same way, or its question would vanish on save.
   */
  const previousGroupIds = useRef<string[]>([]);
  useLayoutEffect(() => {
    const previous = previousGroupIds.current;
    const current = groups.map((group) => group.id);
    previousGroupIds.current = current;
    const replace = (id: string | null) => {
      if (!id || current.includes(id)) return undefined;
      const index = previous.indexOf(id);
      return index >= 0 ? current[index] : undefined;
    };
    const active = replace(activeGroupId);
    if (active) setActiveGroupId(active);
    const removing = replace(removingGroupId);
    if (removing) setRemovingGroupId(removing);
  });
  /*
    Rooms get the same swap, in place. A card opened under its temporary id
    has to stay open under the server's, or the first save snaps it shut
    while the estimator is still typing in it.
  */
  const previousRoomIds = useRef<string[]>([]);
  useLayoutEffect(() => {
    const previous = previousRoomIds.current;
    const current = quote.rooms.map((room) => room.id);
    previousRoomIds.current = current;
    if (previous.length !== current.length) return;
    const carried: Record<string, boolean> = {};
    previous.forEach((id, index) => {
      const replacement = current[index];
      if (replacement && replacement !== id && !isPersistedId(id) && id in opened) {
        carried[replacement] = opened[id] ?? false;
      }
    });
    if (Object.keys(carried).length > 0) setOpened((state) => ({ ...state, ...carried }));
  });
  const addRoomHeading = useRef<HTMLHeadingElement>(null);
  const motion = useListMotion();
  const templates = useQuery({ queryKey: ['room-templates'], queryFn: () => templateApi.rooms() });
  const services = useQuery({ queryKey: ['service-templates'], queryFn: () => templateApi.services() });
  const savedGroups = useQuery({
    queryKey: ['group-templates'],
    queryFn: () => templateApi.groups(),
    enabled: !readOnly,
  });

  function mutateRooms(mutate: (rooms: QuoteRoom[]) => QuoteRoom[]) {
    update((draft) => ({
      ...draft,
      rooms: mutate(draft.rooms).map((room, index) => ({ ...room, displayOrder: index })),
    }));
  }

  function placeRoom(room: QuoteRoom) {
    const placed = showGroups && activeGroup ? { ...room, groupId: activeGroup.id } : room;
    mutateRooms((rooms) => insertInGroup(rooms, placed));
  }

  /*
    The cards of one room type that the add button and its minus work on:
    the selected unit's when units are on, since that is where "+" puts a room.
  */
  function cardsOfType(templateId: string): { room: QuoteRoom; index: number }[] {
    const unitId = showGroups && activeGroup ? activeGroup.id : null;
    return quote.rooms
      .map((room, index) => ({ room, index }))
      .filter(
        ({ room }) => room.roomTemplateId === templateId && (unitId === null || roomGroupId(room, groups) === unitId),
      );
  }

  /*
    The card "+" grows and "−" shrinks: the one counting the most rooms, the
    later of equals. A bedroom listed on its own beside "Bedroom ×4" is the
    one that differs, so the count comes off the four first.
  */
  function countedCard(
    cards: readonly { room: QuoteRoom; index: number }[],
  ): { room: QuoteRoom; index: number } | undefined {
    let counted: { room: QuoteRoom; index: number } | undefined;
    for (const card of cards) {
      if (!counted || roomQuantity(card.room) >= roomQuantity(counted.room)) counted = card;
    }
    return counted;
  }

  /*
    A card whose time was typed in Pricing keeps that time whatever it
    counts. Another room on it would be free and one off it would cost the
    same, so the add button's count leaves it alone while it can.
  */
  function timedFromTasks(card: { room: QuoteRoom }): boolean {
    return card.room.adjustedMinutes == null;
  }

  /*
    "+" follows how the type is already listed. One card, or a card already
    counting several, gets a higher count, so a layout's "Bedroom ×4" becomes
    ×5 rather than ×4 plus a stray card. Bedrooms listed one by one get one
    more card. "List one separately" on a card is how one room gets its own.
  */
  function addFromTemplate(template: RoomTemplate) {
    const count = Math.min(MAX_ROOM_QUANTITY, Math.max(1, addCount));
    const cards = cardsOfType(template.id).filter(timedFromTasks);
    const counted = countedCard(cards);
    if (
      counted &&
      (cards.length === 1 || roomQuantity(counted.room) > 1) &&
      roomQuantity(counted.room) + count <= MAX_ROOM_QUANTITY
    ) {
      patchRoom(counted.index, { quantity: roomQuantity(counted.room) + count });
      setAddCount(1);
      return;
    }
    const room = roomFromTemplate(template, services.data?.items ?? [], flatPricesApply);
    room.quantity = count;
    placeRoom(room);
    setAddCount(1);
  }

  /** The add button's minus: one fewer on the counted card, or that card gone (with Undo) at one. */
  function removeOneOf(template: RoomTemplate) {
    const cards = cardsOfType(template.id);
    const counted = countedCard(cards.filter(timedFromTasks)) ?? countedCard(cards);
    if (!counted) return;
    const quantity = roomQuantity(counted.room);
    if (quantity > 1) patchRoom(counted.index, { quantity: quantity - 1 });
    else removeNow(counted.index);
  }

  function addCustom() {
    const name = customName.trim();
    if (!name) return;
    const room = blankRoom(name);
    room.quantity = Math.min(MAX_ROOM_QUANTITY, Math.max(1, addCount));
    placeRoom(room);
    setCustomName('');
    setAddCount(1);
  }

  function writeGroups(next: QuoteGroup[], rooms: QuoteRoom[]) {
    update((draft) => ({
      ...draft,
      groups: next.map((group, index) => ({ ...group, displayOrder: index })),
      rooms: attachOrphans(rooms, next).map((room, index) => ({ ...room, displayOrder: index })),
    }));
  }

  function addGroup() {
    const created: QuoteGroup = {
      id: temporaryId(),
      name: '',
      nameTranslations: null,
      quantity: 1,
      notes: null,
      isDefault: false,
      displayOrder: groups.length,
    };
    writeGroups([...groups, created], quote.rooms);
    setActiveGroupId(created.id);
  }

  function patchGroup(groupId: string, patch: Partial<QuoteGroup>) {
    writeGroups(
      groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              ...patch,
              nameTranslations: patch.name !== undefined ? null : group.nameTranslations,
            }
          : group,
      ),
      quote.rooms,
    );
  }

  function duplicateGroup(groupId: string) {
    const source = groups.find((group) => group.id === groupId);
    if (!source) return;
    const copyId = temporaryId();
    const copy: QuoteGroup = {
      ...source,
      id: copyId,
      name: source.name.trim()
        ? t('roomsStep.copySuffix', { name: source.name })
        : t('roomsStep.copySuffix', { name: t('roomsStep.ungroupedName') }),
      nameTranslations: null,
      isDefault: false,
    };
    const copies = quote.rooms
      .filter((room) => roomGroupId(room, groups) === groupId)
      .map((room) => ({
        ...room,
        id: temporaryId(),
        groupId: copyId,
        services: room.services.map((service) => ({ ...service, id: temporaryId() })),
      }));
    const index = groups.findIndex((group) => group.id === groupId);
    const next = [...groups.slice(0, index + 1), copy, ...groups.slice(index + 1)];
    writeGroups(next, [...quote.rooms, ...copies]);
    setActiveGroupId(copyId);
  }

  function removeGroup(groupId: string) {
    if (groups.length <= 1) return;
    let remaining = groups.filter((group) => group.id !== groupId);
    if (!remaining.some((group) => group.isDefault)) {
      remaining = remaining.map((group, index) => ({ ...group, isDefault: index === 0 }));
    }
    const fallback = defaultGroupOf(remaining)?.id ?? remaining[0]?.id ?? '';
    const rooms = quote.rooms.map((room) =>
      roomGroupId(room, groups) === groupId ? { ...room, groupId: fallback } : room,
    );
    writeGroups(remaining, rooms);
    if (activeGroupId === groupId) setActiveGroupId(fallback);
    setUnitFocus(fallback);
  }

  useEffect(() => {
    if (!unitFocus) return;
    setUnitFocus(null);
    const element = document.querySelector<HTMLElement>(`[data-unit-summary="${unitFocus}"]`);
    (element ?? addRoomHeading.current)?.focus({ preventScroll: true });
  }, [unitFocus]);

  function insertSavedGroup(template: GroupTemplate) {
    const only = groups.length === 1 ? groups[0] : undefined;
    const pristineEmpty =
      only !== undefined &&
      only.id.length > 0 &&
      only.isDefault &&
      clampGroupQuantity(only.quantity) === 1 &&
      only.name.trim().length === 0 &&
      !(only.notes ?? '').trim() &&
      quote.rooms.length === 0;
    // A starter hint ("set the quantity to 40") is not a message for the
    // customer. A note the company wrote on that layout is, seed key or not.
    const customerNotes = customerNotesForSavedGroup(template);
    const created: QuoteGroup = pristineEmpty
      ? {
          ...only,
          name: template.name,
          nameTranslations: template.nameTranslations,
          notes: customerNotes,
          quantity: Math.min(MAX_GROUP_QUANTITY, Math.max(1, addCount)),
          isDefault: true,
        }
      : {
          id: temporaryId(),
          name: template.name,
          nameTranslations: template.nameTranslations,
          quantity: Math.min(MAX_GROUP_QUANTITY, Math.max(1, addCount)),
          notes: customerNotes,
          isDefault: false,
          displayOrder: groups.length,
        };
    const added = template.rooms.map((room) =>
      roomFromGroupSnapshot(room, created.id, services.data?.items ?? [], templates.data?.items ?? [], flatPricesApply),
    );
    writeGroups(pristineEmpty ? [created] : [...groups, created], [...quote.rooms, ...added]);
    setActiveGroupId(created.id);
    setAddCount(1);
  }

  /**
   * A layout as the starting point of an ordinary quote: a 4-bedroom house
   * dropped in, then trimmed to the job. Its rooms join the quote's own room
   * list, with no unit name or count, so the customer sees rooms rather than
   * a "4-bedroom house" section and square-foot pricing still applies.
   *
   * The note the company wrote on the layout is for the customer, and with no
   * unit section to print under it joins the quote's own customer notes.
   */
  function startFromLayout(template: GroupTemplate, mode: 'replace' | 'add') {
    const groupId = fallbackGroup?.id ?? '';
    const added = template.rooms.map((room) =>
      roomFromGroupSnapshot(room, groupId, services.data?.items ?? [], templates.data?.items ?? [], flatPricesApply),
    );
    const note = customerNotesForSavedGroup(template)?.trim() ?? '';
    // The notes with the layout's note added, or null when it is already there
    // (a paragraph of its own, not words inside another note) or nothing to add.
    const withNote = (current: string | null | undefined): string | null => {
      const existing = current?.trim() ?? '';
      if (!note || existing.split(/\n\s*\n/).some((paragraph) => paragraph.trim() === note)) return null;
      return existing ? `${existing}\n\n${note}` : note;
    };
    // A note that would not fit is left off rather than making every save fail.
    const planned = withNote(quote.customerNotes);
    const noteFits = planned === null || planned.length <= MAX_QUOTE_CUSTOMER_NOTES_LENGTH;
    setLayoutStatus(noteFits ? null : t('quoteWork.layoutNoteTooLong'));
    update((draft) => {
      const joined = withNote(draft.customerNotes);
      const customerNotes =
        joined !== null && joined.length <= MAX_QUOTE_CUSTOMER_NOTES_LENGTH ? joined : draft.customerNotes;
      const kept = mode === 'add' ? draft.rooms : [];
      return {
        ...draft,
        customerNotes,
        rooms: attachOrphans([...kept, ...added], quoteGroups(draft)).map((room, index) => ({
          ...room,
          displayOrder: index,
        })),
      };
    });
    setLayoutPending(null);
    setAddCount(1);
  }

  // Adding to the rooms already here cannot take the quote past what a save accepts.
  const addingOverflows = layoutPending !== null && quote.rooms.length + layoutPending.rooms.length > MAX_QUOTE_ROOMS;

  function pickLayout(template: GroupTemplate) {
    if (showGroups) {
      insertSavedGroup(template);
      return;
    }
    if (quote.rooms.length === 0) {
      startFromLayout(template, 'add');
      return;
    }
    setLayoutMode('replace');
    setLayoutPending(template);
  }

  async function saveLayout() {
    if (!layoutDraft || layoutDraft.busy) return;
    const name = layoutDraft.name.trim();
    if (!name) {
      setLayoutDraft({ ...layoutDraft, error: t('groupTemplates.needsName') });
      return;
    }
    setLayoutDraft({ ...layoutDraft, busy: true, error: null });
    try {
      await templateApi.createGroup({
        name,
        // The name was typed in the language on screen. Room names keep the
        // quote's language and their own translations.
        textLanguage: screenLanguage,
        tags: { segments: [quote.segment], jobTypes: [], floorTypes: [] },
        rooms: quote.rooms.map((room) => layoutRoomFromQuoteRoom(room, flatPricesApply)),
      });
      setLayoutDraft(null);
      setLayoutStatus(t('quoteWork.layoutSaved', { name }));
      await queryClient.invalidateQueries({ queryKey: ['group-templates'] });
    } catch (caught) {
      setLayoutDraft({
        name: layoutDraft.name,
        busy: false,
        error: caught instanceof ApiRequestError ? caught.message : t('groupTemplates.saveError'),
      });
    }
  }

  function removeNow(index: number) {
    const room = quote.rooms[index];
    if (!room) return;
    setUndo({ room, index });
    mutateRooms((rooms) => rooms.filter((_, roomIndex) => roomIndex !== index));
  }

  function restoreRemoved() {
    if (!undo) return;
    const { room, index } = undo;
    setUndo(null);
    // A save may already have deleted the row. Putting the old id back makes
    // the next save reject the room. A fresh id is a new room with the same work.
    const restored: QuoteRoom = isPersistedId(room.id)
      ? {
          ...room,
          id: temporaryId(),
          services: room.services.map((service) =>
            isPersistedId(service.id) ? { ...service, id: temporaryId() } : service,
          ),
        }
      : room;
    mutateRooms((rooms) => {
      const next = [...rooms];
      next.splice(Math.min(index, next.length), 0, restored);
      return next;
    });
  }

  /*
    Every card starts closed, the first one included. Closed, a card is one
    line that already says what matters (tasks, time, anything not typical),
    so the page reads as the list of rooms rather than one room's form and a
    long scroll to the rest. A card opens when tapped or for "Choose tasks".
  */
  function roomIsOpen(roomId: string) {
    return opened[roomId] ?? false;
  }

  function setAllOpen(open: boolean) {
    setOpened(Object.fromEntries(quote.rooms.map((room) => [room.id, open])));
  }

  function chooseTasks(roomId: string) {
    setOpened((current) => ({ ...current, [roomId]: true }));
    setTasksFocus((current) => ({ roomId, token: (current?.token ?? 0) + 1 }));
    onOpenTasks(roomId);
  }

  useEffect(() => {
    if (!tasksFocus) return;
    const element = document.getElementById(`quote-tasks-${tasksFocus.roomId}`);
    if (!element) return;
    element.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    element.focus({ preventScroll: true });
  }, [tasksFocus]);

  function duplicate(index: number) {
    mutateRooms((rooms) => {
      const source = rooms[index];
      if (!source) return rooms;
      const copy: QuoteRoom = {
        ...source,
        id: temporaryId(),
        // Translated: the suffix is persisted into the quote, and a Spanish
        // estimator's "Cocina (copy)" would ship to the customer.
        roomName: t('roomsStep.copySuffix', { name: source.roomName }),
        // And suffixed in each language the room carries, each with that
        // language's own word for a copy — `lng` asks i18next for a specific
        // catalog rather than the one on screen (see localized-text.ts). A room with no map
        // keeps none: one string in, one string out.
        roomNameTranslations: source.roomNameTranslations
          ? Object.fromEntries(
              Object.entries(source.roomNameTranslations).map(([language, name]) => [
                language,
                t('roomsStep.copySuffix', { name, lng: language }),
              ]),
            )
          : null,
        services: source.services.map((service) => ({ ...service, id: temporaryId() })),
      };
      return [...rooms.slice(0, index + 1), copy, ...rooms.slice(index + 1)];
    });
  }

  /*
    One room of a counted card gets a card of its own: same tasks and
    settings, right below, open with its name ready to change ("Kids' room").
    The card it came from counts one fewer, so the room count does not move.

    A typed time adjustment is the whole card's time, not each room's, so the
    new card does not take it: it is timed from its tasks like any one room.
  */
  function splitOne(index: number) {
    const source = quote.rooms[index];
    if (!source || roomQuantity(source) <= 1 || quote.rooms.length >= MAX_QUOTE_ROOMS) return;
    const id = temporaryId();
    mutateRooms((rooms) => {
      const current = rooms[index];
      if (!current || roomQuantity(current) <= 1) return rooms;
      const single: QuoteRoom = {
        ...current,
        id,
        quantity: 1,
        adjustedMinutes: null,
        isManuallyAdjusted: false,
        services: current.services.map((service) => ({ ...service, id: temporaryId() })),
      };
      return [
        ...rooms.slice(0, index),
        { ...current, quantity: roomQuantity(current) - 1 },
        single,
        ...rooms.slice(index + 1),
      ];
    });
    setOpened((current) => ({ ...current, [id]: true }));
    setNameFocus(id);
  }

  useEffect(() => {
    if (!nameFocus) return;
    const element = document.querySelector<HTMLInputElement>(`[data-room-name="${nameFocus}"]`);
    if (!element) return;
    setNameFocus(null);
    element.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    element.focus({ preventScroll: true });
    element.select();
  }, [nameFocus, quote.rooms]);

  function move(index: number, delta: number) {
    mutateRooms((rooms) => {
      const room = rooms[index];
      if (!room) return rooms;
      if (!showGroups) {
        const target = index + delta;
        if (target < 0 || target >= rooms.length) return rooms;
        const next = [...rooms];
        const [moved] = next.splice(index, 1);
        if (moved) next.splice(target, 0, moved);
        return next;
      }
      const groupId = roomGroupId(room, groups);
      const indexes = rooms.flatMap((candidate, candidateIndex) =>
        roomGroupId(candidate, groups) === groupId ? [candidateIndex] : [],
      );
      const position = indexes.indexOf(index);
      const neighbor = indexes[position + delta];
      if (neighbor === undefined) return rooms;
      const next = [...rooms];
      const current = next[index];
      const other = next[neighbor];
      if (!current || !other) return rooms;
      next[index] = other;
      next[neighbor] = current;
      return next;
    });
  }

  function canMove(index: number, delta: number): boolean {
    const room = quote.rooms[index];
    if (!room) return false;
    if (!showGroups) {
      const target = index + delta;
      return target >= 0 && target < quote.rooms.length;
    }
    const groupId = roomGroupId(room, groups);
    const indexes = quote.rooms.flatMap((candidate, candidateIndex) =>
      roomGroupId(candidate, groups) === groupId ? [candidateIndex] : [],
    );
    const position = indexes.indexOf(index);
    return indexes[position + delta] !== undefined;
  }

  function patchRoom(index: number, patch: Partial<QuoteRoom>) {
    mutateRooms((rooms) => rooms.map((room, i) => (i === index ? { ...room, ...patch } : room)));
  }

  function moveRoomToGroup(index: number, groupId: string) {
    const room = quote.rooms[index];
    if (!room || roomGroupId(room, groups) === groupId) return;
    mutateRooms((rooms) => {
      const current = rooms[index];
      if (!current) return rooms;
      const without = rooms.filter((_, roomIndex) => roomIndex !== index);
      return insertInGroup(without, { ...current, groupId });
    });
  }

  /*
    How many of each room type the quote already holds, keyed by template.

    The add buttons sit above a list that can run well past the fold, so
    without this the only way to tell whether "+ Bedroom" has been pressed is
    to scroll. Quantity counts rather than lines: one bedroom line set to
    "How many: 3" is three bedrooms, which is what the estimator is tracking.
  */
  const addedByTemplate = new Map<string, number>();
  for (const room of quote.rooms) {
    if (!room.roomTemplateId) continue;
    const quantity = Math.max(1, Math.trunc(room.quantity) || 1);
    addedByTemplate.set(room.roomTemplateId, (addedByTemplate.get(room.roomTemplateId) ?? 0) + quantity);
  }

  // Times each unit's count: one bedroom in "Apartment ×40" is forty bedrooms of work.
  const totalRooms = quote.rooms.reduce((sum, room) => {
    const unit = groups.find((group) => group.id === roomGroupId(room, groups));
    return sum + Math.max(1, Math.trunc(room.quantity) || 1) * clampGroupQuantity(unit?.quantity);
  }, 0);
  const allOpen = quote.rooms.length > 0 && quote.rooms.every((room) => roomIsOpen(room.id));

  const sections = showGroups
    ? groups.map((group) => ({
        group,
        rooms: quote.rooms
          .map((room, index) => ({ room, index }))
          .filter(({ room }) => roomGroupId(room, groups) === group.id),
      }))
    : [{ group: null as QuoteGroup | null, rooms: quote.rooms.map((room, index) => ({ room, index })) }];

  const undoNotice = undo ? (
    <p className="mt-4 flex flex-wrap items-center gap-3 text-sm" role="status">
      <span>{t('quoteWork.removed', { name: roomLabel(undo.room) })}</span>
      <button type="button" className="min-h-11 font-semibold text-[color:var(--color-brand)]" onClick={restoreRemoved}>
        {t('quoteWork.undo')}
      </button>
    </p>
  ) : null;

  /*
    The phone's order: the rooms in the quote come first, and adding more is
    one button under them, which opens the whole picker. An empty quote opens
    on the picker, since adding is the only thing to do there.
  */
  const addPanel = readOnly ? null : addOpen || !listFirst ? (
    <section className={classes('card', listFirst ? 'mt-6' : 'mb-6')} aria-labelledby="add-room-heading">
      {/*
          `tabIndex={-1}` so focus can be put here after the last room is
          removed. Not reachable by Tab — only programmatically.
        */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 id="add-room-heading" ref={addRoomHeading} tabIndex={-1} className="text-lg font-semibold">
          {t('quoteWork.addHeading')}
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <QuantityStepper
            label={t('quoteWork.howMany')}
            value={addCount}
            min={1}
            max={showGroups ? MAX_GROUP_QUANTITY : MAX_ROOM_QUANTITY}
            onChange={setAddCount}
          />
          {listFirst ? (
            <Button variant="ghost" onClick={() => setAddOpen(false)}>
              {t('quoteWork.doneAdding')}
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">
        {quote.rooms.length === 0
          ? t('quoteWork.emptyHint')
          : showGroups
            ? t('quoteWork.addHint')
            : t('quoteWork.addHintRoom')}
      </p>
      {showGroups && activeGroup ? (
        <div className="mb-4 max-w-md">
          <Select
            label={t('roomsStep.selectGroup')}
            value={activeGroup.id}
            onChange={(event) => setActiveGroupId(event.target.value)}
            options={groups.map((group) => ({
              value: group.id,
              label: group.name.trim() || t('roomsStep.ungroupedName'),
            }))}
          />
        </div>
      ) : null}
      {(savedGroups.data?.items.length ?? 0) > 0 ? (
        /*
            Open on an empty quote, where starting from a layout is the
            likely first move. It closes once rooms are in, so the room
            buttons below come back into view.
          */
        <details className="mb-4" open={(quote.rooms.length === 0 && !showGroups) || undefined}>
          <summary className="mb-2 min-h-11 cursor-pointer text-sm font-semibold">
            {showGroups ? t('quoteWork.layoutAsUnit') : t('quoteWork.startFromLayout')}
          </summary>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">
            {showGroups ? t('quoteWork.layoutAsUnitHint') : t('quoteWork.startFromLayoutHint')}
          </p>
          <TemplatePicker
            items={savedGroups.data?.items ?? []}
            label={(template) => localized.text(template.name, template.nameTranslations)}
            categories={ROOM_CATEGORIES.map((code) => ({
              code,
              label: t(`libraryTaxonomy.room.${code}`),
            }))}
            onPick={pickLayout}
            contextSegment={quote.segment}
            segments={everyLayout ? ['residential', 'commercial'] : segmentsForQuote(quote.segment, showGroups)}
            disabled={!services.data || !templates.data}
          />
          <Checkbox
            label={t('quoteWork.everyLayout')}
            checked={everyLayout}
            onChange={(event) => setEveryLayout(event.target.checked)}
          />
        </details>
      ) : null}

      {templates.isLoading || services.isLoading ? <LoadingBlock label={t('roomsStep.loadingRoomTypes')} /> : null}

      {/*
          Both queries have to be in before a template button does anything
          useful: the room list can arrive first, and adding from a template
          while the task library is still loading would silently create a room
          with no tasks and no time.
        */}
      <TemplatePicker
        items={templates.data?.items ?? []}
        disabled={!services.data}
        label={(template) => `+ ${localized.text(template.name, template.nameTranslations)}`}
        categories={ROOM_CATEGORIES.map((code) => ({
          code,
          label: t(`libraryTaxonomy.room.${code}`),
        }))}
        badge={(template) => {
          const added = addedByTemplate.get(template.id) ?? 0;
          return added > 0 ? String(added) : null;
        }}
        nameFor={(template) => {
          const added = addedByTemplate.get(template.id) ?? 0;
          if (added <= 0) return undefined;
          return t('roomsStep.addAnotherWithCount', {
            room: localized.text(template.name, template.nameTranslations),
            added,
          });
        }}
        onPick={addFromTemplate}
        onRemove={removeOneOf}
        removeNameFor={(template) => {
          if (cardsOfType(template.id).length === 0) return null;
          return t('roomsStep.removeOneWithCount', {
            room: localized.text(template.name, template.nameTranslations),
            added: addedByTemplate.get(template.id) ?? 0,
          });
        }}
        contextSegment={quote.segment}
        segments={segmentsForQuote(quote.segment, showGroups)}
      />

      {services.isError ? (
        <p className="field-error" role="alert">
          {t('roomsStep.taskLibraryError')}
        </p>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1 basis-56">
          <TextInput
            label={t('roomsStep.customAreaLabel')}
            placeholder={t('roomsStep.customAreaPlaceholder')}
            value={customName}
            onChange={(event) => setCustomName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addCustom();
              }
            }}
          />
        </div>
        <div className="mb-4">
          <Button onClick={addCustom} disabled={!customName.trim()}>
            {t('roomsStep.addArea')}
          </Button>
        </div>
      </div>

      {!showGroups ? (
        <div className="mt-2 flex flex-wrap gap-x-6">
          <button
            type="button"
            className="min-h-11 text-sm font-semibold text-[color:var(--color-brand)]"
            onClick={() => setUnitsOn(true)}
          >
            {t('quoteWork.addUnitType')}
          </button>
          {/* A layout holds fewer rooms than a quote can. */}
          {canSaveLayout && quote.rooms.length > 0 && quote.rooms.length <= MAX_LAYOUT_ROOMS ? (
            <button
              type="button"
              className="min-h-11 text-sm font-semibold text-[color:var(--color-brand)]"
              onClick={() => {
                setLayoutStatus(null);
                setLayoutDraft({ name: '', busy: false, error: null });
              }}
            >
              {t('quoteWork.saveLayout')}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="mt-2">
          <Button onClick={addGroup}>{t('quoteWork.addAnotherUnit')}</Button>
        </div>
      )}

      {layoutStatus ? (
        <p className="mt-2 text-sm" role="status">
          {layoutStatus}
        </p>
      ) : null}

      {layoutPending ? (
        <ConfirmDialog
          title={t('quoteWork.layoutReplaceTitle', {
            name: localized.text(layoutPending.name, layoutPending.nameTranslations),
          })}
          confirmLabel={t('quoteWork.layoutUse')}
          confirmDisabled={layoutMode === 'add' && addingOverflows}
          onCancel={() => setLayoutPending(null)}
          onConfirm={() => startFromLayout(layoutPending, layoutMode)}
        >
          <fieldset className="grid gap-2">
            <legend className="mb-2">{t('quoteWork.layoutReplaceBody')}</legend>
            {(['replace', 'add'] as const).map((mode) => (
              <label key={mode} className="flex min-h-11 cursor-pointer items-center gap-3">
                <input
                  type="radio"
                  name="layout-mode"
                  checked={layoutMode === mode}
                  disabled={mode === 'add' && addingOverflows}
                  onChange={() => setLayoutMode(mode)}
                />
                <span>{mode === 'replace' ? t('quoteWork.layoutReplace') : t('quoteWork.layoutAdd')}</span>
              </label>
            ))}
            {addingOverflows ? (
              <p className="field-hint">{t('quoteWork.layoutTooManyRooms', { max: MAX_QUOTE_ROOMS })}</p>
            ) : null}
          </fieldset>
        </ConfirmDialog>
      ) : null}

      {layoutDraft ? (
        <ConfirmDialog
          title={t('quoteWork.saveLayoutTitle')}
          confirmLabel={t('quoteWork.saveLayoutAction')}
          busy={layoutDraft.busy}
          error={layoutDraft.error}
          onCancel={() => setLayoutDraft(null)}
          onConfirm={() => void saveLayout()}
        >
          <p className="mb-3">{t('quoteWork.saveLayoutBody')}</p>
          <TextInput
            label={t('groupTemplates.nameLabel')}
            value={layoutDraft.name}
            onChange={(event) => setLayoutDraft({ ...layoutDraft, name: event.target.value, error: null })}
          />
        </ConfirmDialog>
      ) : null}

      {undoNotice}
    </section>
  ) : (
    <section className="card mt-6" aria-labelledby="add-room-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="add-room-heading" ref={addRoomHeading} tabIndex={-1} className="text-lg font-semibold">
            {t('quoteWork.addHeading')}
          </h2>
          <p className="text-sm text-[color:var(--color-muted)]">{t('quoteWork.addClosedHint')}</p>
        </div>
        <Button variant="primary" onClick={() => setAddOpen(true)}>
          {t('quoteWork.addRoomsButton')}
        </Button>
      </div>
      {undoNotice}
    </section>
  );

  const roomList = (
    <>
      {quote.rooms.length === 0 && !showGroups ? (
        <EmptyState title={t('roomsStep.emptyTitle')}>
          <p>{t('roomsStep.emptyMessage')}</p>
        </EmptyState>
      ) : (
        <div className="space-y-6">
          {quote.rooms.length > 0 ? (
            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
              <div>
                <h2 className="text-lg font-semibold">
                  {t('roomsStep.addedHeading')}{' '}
                  <span className="font-normal text-[color:var(--color-muted)]">
                    {t('roomsStep.roomCount', { count: totalRooms })}
                  </span>
                </h2>
                <p className="text-sm text-[color:var(--color-muted)]">
                  {readOnly ? t('roomsStep.listHintReadOnly') : t('roomsStep.listHint')}
                </p>
              </div>
              <Button variant="ghost" onClick={() => setAllOpen(!allOpen)}>
                {allOpen ? t('roomsStep.closeAll') : t('roomsStep.openAll')}
              </Button>
            </div>
          ) : null}
          {sections.map((section) => {
            const indexes = section.rooms.map(({ index }) => index);
            const calculationInput = calculationInputFor(quote);
            const subtotal = section.group ? groupSubtotals(calculationInput, [indexes])[0] : undefined;
            const groupQuantity = section.group ? clampGroupQuantity(section.group.quantity) : 1;
            const eachMinutes =
              subtotal && groupQuantity > 1 ? Math.round(subtotal.minutes / groupQuantity) : (subtotal?.minutes ?? 0);
            const roomCount = section.rooms.reduce(
              (sum, { room }) => sum + Math.max(1, Math.trunc(room.quantity) || 1),
              0,
            );
            const groupName = section.group?.name.trim() || t('quoteWork.allRooms');
            return (
              <section
                key={section.group?.id ?? 'flat'}
                aria-label={section.group ? groupName : undefined}
                onDragOver={(event) => {
                  if (!section.group) return;
                  event.preventDefault();
                }}
                onDrop={(event) => {
                  if (!section.group) return;
                  event.preventDefault();
                  const index = Number(event.dataTransfer.getData('text/plain'));
                  if (Number.isInteger(index)) moveRoomToGroup(index, section.group.id);
                }}
              >
                {section.group ? (
                  <details className="card mb-4" open>
                    <summary className="cursor-pointer text-base font-semibold" data-unit-summary={section.group.id}>
                      {t(roomCount === 1 ? 'quoteWork.groupSummaryOne' : 'quoteWork.groupSummary', {
                        name: groupName,
                        count: groupQuantity,
                        rooms: roomCount,
                        time: formatMinutes(eachMinutes),
                        amount: calculationInput.pricingBlocked
                          ? t('areaPricing.unpriced')
                          : formatCents(subtotal?.cents ?? 0, quote.currencyCode, getDateLocale()),
                      })}
                    </summary>
                    <div className="mt-4 grid gap-x-4 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <TextInput
                        label={t('quoteWork.unitName')}
                        placeholder={t('quoteWork.allRooms')}
                        value={section.group.name}
                        disabled={readOnly}
                        onChange={(event) => patchGroup(section.group!.id, { name: event.target.value })}
                      />
                      <QuantityStepper
                        label={t('quoteWork.howMany')}
                        value={section.group.quantity}
                        min={1}
                        max={MAX_GROUP_QUANTITY}
                        disabled={readOnly}
                        onChange={(quantity) => patchGroup(section.group!.id, { quantity })}
                      />
                    </div>
                    <details className="mb-3">
                      <summary className="cursor-pointer text-sm font-semibold">{t('quoteWork.more')}</summary>
                      <TextArea
                        label={t('roomsStep.groupNotesLabel')}
                        hint={t('roomsStep.groupNotesHint')}
                        rows={2}
                        value={section.group.notes ?? ''}
                        disabled={readOnly}
                        onChange={(event) => patchGroup(section.group!.id, { notes: event.target.value || null })}
                      />
                    </details>
                    {!readOnly ? (
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => duplicateGroup(section.group!.id)}>
                          {t('quoteWork.duplicateUnit')}
                        </Button>
                        {groups.length > 1 ? (
                          <Button
                            variant="danger"
                            onClick={() => {
                              const group = section.group!;
                              // Nothing to lose: no rooms, no name, no notes, counted once.
                              if (
                                section.rooms.length === 0 &&
                                !group.name.trim() &&
                                !(group.notes ?? '').trim() &&
                                clampGroupQuantity(group.quantity) === 1
                              ) {
                                removeGroup(group.id);
                              } else {
                                setRemovingGroupId(group.id);
                              }
                            }}
                          >
                            {t('quoteWork.removeUnit')}
                          </Button>
                        ) : null}
                        {removingGroupId === section.group.id ? (
                          <ConfirmDialog
                            title={t('quoteWork.removeUnitTitle', { name: groupName })}
                            confirmLabel={t('quoteWork.removeUnit')}
                            confirmVariant="danger"
                            initialFocus="cancel"
                            onCancel={() => setRemovingGroupId(null)}
                            onConfirm={() => {
                              setRemovingGroupId(null);
                              removeGroup(section.group!.id);
                            }}
                          >
                            <p>{t('quoteWork.removeUnitBody', { quantity: groupQuantity })}</p>
                            {section.rooms.length > 0
                              ? (() => {
                                  const target = defaultGroupOf(
                                    groups.filter((group) => group.id !== section.group!.id),
                                  );
                                  // The count they will be priced at there, not a price: a
                                  // fixed total, for one, would not move.
                                  return (
                                    <p className="mt-2">
                                      {t('quoteWork.removeUnitRoomsMove', {
                                        target: target?.name.trim() || t('quoteWork.allRooms'),
                                        quantity: clampGroupQuantity(target?.quantity ?? 1),
                                      })}
                                    </p>
                                  );
                                })()
                              : null}
                          </ConfirmDialog>
                        ) : null}
                      </div>
                    ) : null}
                  </details>
                ) : null}
                {section.rooms.length === 0 ? (
                  <p className="text-sm text-[color:var(--color-muted)]">{t('roomsStep.groupEmpty')}</p>
                ) : (
                  <ol className="space-y-4">
                    {section.rooms.map(({ room, index }) => {
                      const selectedTasks = room.services.filter((service) => service.isSelected).length;
                      const open = roomIsOpen(room.id);
                      const roomInput = calculationInput.rooms[index];
                      const minutes = roomInput ? calculateRoom(roomInput).billableMinutes : 0;
                      // Only what differs from a typical room: "Medium · Average" on every card is noise.
                      const notTypical = [
                        room.sizeCode !== 'medium' && ROOM_SIZES.some((size) => size.code === room.sizeCode)
                          ? t(`room.size.${room.sizeCode}`)
                          : null,
                        room.conditionCode !== 'average' &&
                        ROOM_CONDITIONS.some((condition) => condition.code === room.conditionCode)
                          ? t(`room.condition.${room.conditionCode}`)
                          : null,
                      ].filter((part): part is string => part !== null);
                      // Wraps after a dot, never inside a part ("1 / hr 15 min").
                      const summary = [
                        t('roomsStep.tasksSelected', { selected: selectedTasks, total: room.services.length }),
                        ...[formatMinutes(minutes), ...notTypical].map((part) => part.replace(/ /g, '\u00a0')),
                      ].join('\u00a0· ');
                      return (
                        <li
                          key={room.id}
                          ref={motion.register(cardMotionKey(room.id))}
                          className={classes('card', !open && 'py-3')}
                          draggable={!readOnly}
                          onDragStart={(event) => {
                            event.dataTransfer.setData('text/plain', String(index));
                            event.dataTransfer.effectAllowed = 'move';
                          }}
                        >
                          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3 sm:flex sm:flex-wrap sm:items-center sm:gap-2">
                            {/*
                            Two spans, as before cards started closed: every
                            node in here is walked again for each card's name.
                            The arrow is drawn in CSS so it adds none, and has
                            no character for a screen reader to read out.
                          */}
                            <button
                              type="button"
                              className={classes(
                                'relative col-span-2 min-h-11 min-w-0 pl-5 text-left sm:flex-1 sm:basis-56',
                                "before:absolute before:left-0.5 before:top-[0.45rem] before:border-y-[5px] before:border-l-[7px] before:border-y-transparent before:border-l-[color:var(--color-muted)] before:transition-transform before:content-['']",
                                open && 'before:rotate-90',
                              )}
                              aria-expanded={open}
                              onClick={() => setOpened((current) => ({ ...current, [room.id]: !open }))}
                            >
                              <span className="block font-semibold">{roomLabel(room)}</span>
                              <span className="block text-sm text-[color:var(--color-muted)]">{summary}</span>
                            </button>
                            <Button className="col-span-2" onClick={() => chooseTasks(room.id)}>
                              {t('roomsStep.chooseTasks')}
                            </Button>
                            <div className="col-span-2 flex flex-wrap items-end justify-between gap-3 sm:contents">
                              <QuantityStepper
                                label={t('roomsStep.howManyLabel')}
                                value={room.quantity}
                                min={1}
                                max={MAX_ROOM_QUANTITY}
                                disabled={readOnly}
                                onChange={(quantity) => patchRoom(index, { quantity })}
                              />
                              {!readOnly ? (
                                <button
                                  type="button"
                                  className="ml-auto inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-md text-lg text-[color:var(--color-muted)] hover:text-[color:var(--color-danger)] sm:ml-0"
                                  aria-label={t('roomsStep.removeRoomCard', { room: roomLabel(room) })}
                                  onClick={() => removeNow(index)}
                                >
                                  <span aria-hidden="true">×</span>
                                </button>
                              ) : null}
                            </div>
                          </div>
                          {open ? (
                            <div className="mt-4">
                              <div className="min-w-0">
                                {/*
                    Bold, like the room's name is on the button that added it
                    and on the chip above. It is the heading of this block in
                    every way but the markup, and a normal-weight value here
                    made the same room read as two different things depending
                    on where you were looking at it.
                  */}
                                {/*
                    Shown in the QUOTE's language — the name the customer will
                    read — and typing in it drops the other languages.

                    Dropping them looks lossy and is the honest answer: what
                    has been typed is one string, in one language, and keeping
                    the old Spanish beside a new English name would leave the
                    crew sheet naming a room this quote no longer has. Picking
                    the room type again brings both back.
                  */}
                                <TextInput
                                  label={t('roomsStep.roomNameLabel')}
                                  className="font-semibold"
                                  data-room-name={room.id}
                                  value={roomLabel(room)}
                                  disabled={readOnly}
                                  onChange={(event) =>
                                    patchRoom(index, { roomName: event.target.value, roomNameTranslations: null })
                                  }
                                />
                              </div>
                              {showGroups ? (
                                <div className="min-w-0 flex-1">
                                  <Select
                                    label={t('roomsStep.roomGroupLabel')}
                                    value={roomGroupId(room, groups)}
                                    disabled={readOnly}
                                    onChange={(event) => moveRoomToGroup(index, event.target.value)}
                                    options={groups.map((group) => ({
                                      value: group.id,
                                      label: group.name.trim() || t('roomsStep.ungroupedName'),
                                    }))}
                                  />
                                </div>
                              ) : null}

                              <div className="grid gap-x-4 sm:grid-cols-2">
                                <Select
                                  label={t('roomsStep.sizeLabel')}
                                  value={room.sizeCode}
                                  disabled={readOnly}
                                  hint={
                                    ROOM_SIZES.find((size) => size.code === room.sizeCode)
                                      ? t(`room.sizeDescription.${room.sizeCode}`)
                                      : undefined
                                  }
                                  onChange={(event) =>
                                    patchRoom(index, { sizeCode: event.target.value as QuoteRoom['sizeCode'] })
                                  }
                                  options={ROOM_SIZES.map((size) => ({
                                    value: size.code,
                                    label: t(`room.size.${size.code}`),
                                  }))}
                                />
                                <Select
                                  label={t('roomsStep.conditionLabel')}
                                  value={room.conditionCode}
                                  disabled={readOnly}
                                  hint={
                                    ROOM_CONDITIONS.find((condition) => condition.code === room.conditionCode)
                                      ? t(`room.conditionDescription.${room.conditionCode}`)
                                      : undefined
                                  }
                                  onChange={(event) =>
                                    patchRoom(index, {
                                      conditionCode: event.target.value as QuoteRoom['conditionCode'],
                                    })
                                  }
                                  options={ROOM_CONDITIONS.map((condition) => ({
                                    value: condition.code,
                                    label: t(`room.condition.${condition.code}`),
                                  }))}
                                />

                                {/*
                  Empty is not zero. Clearing the box prices the room from its
                  tasks the way it always was; typing 0 in it quotes the room
                  for nothing. `MoneyInput` gives back null for the first and
                  0 for the second, which is exactly the distinction the
                  server stores.
                */}
                                {quote.areaPricing?.mode === 'area' && quote.areaPricing.confirmed ? (
                                  // Priced by square foot: the area price replaces room prices.
                                  <p className="text-sm text-[color:var(--color-muted)]">
                                    {t('roomsStep.fixedPriceAreaHint')}
                                  </p>
                                ) : (
                                  <MoneyInput
                                    currencyCode={quote.currencyCode}
                                    label={t('roomsStep.fixedPriceLabel')}
                                    hint={
                                      room.flatPriceCents === null
                                        ? t('roomsStep.fixedPriceHint')
                                        : t('roomsStep.fixedPriceSetHint', {
                                            price: formatCents(
                                              calculateRoomChargeCents({
                                                flatPriceCents: room.flatPriceCents,
                                                quantity: room.quantity,
                                                sizeMultiplierBasisPoints: sizeMultiplierFor(room.sizeCode),
                                                conditionMultiplierBasisPoints: conditionMultiplierFor(
                                                  room.conditionCode,
                                                ),
                                              }) ?? 0,
                                              quote.currencyCode,
                                              getDateLocale(),
                                            ),
                                          })
                                    }
                                    valueCents={room.flatPriceCents}
                                    disabled={readOnly}
                                    min={0}
                                    onChangeCents={(cents) => patchRoom(index, { flatPriceCents: cents })}
                                  />
                                )}

                                {/*
                  What a commercial bid is actually built from: the area, and
                  what is underfoot in it.

                  Commercial quotes only. A house is estimated room by room
                  from task minutes and always has been; asking a residential
                  estimator for the square footage of a bathroom would be
                  asking for a number nobody has and nothing reads.

                  Empty is not zero here either — an unmeasured area is one
                  nobody has walked yet, and its measured tasks price at
                  nothing until somebody types a real figure, which is
                  visibly different from an area that takes no time.
                */}
                                {quote.segment === 'commercial' ? (
                                  <>
                                    <NumberInput
                                      label={t('roomsStep.squareFeetLabel')}
                                      /*
                        A figure that is not reaching the price says so, on
                        the field that took it. The box shows on every
                        commercial area whether or not anything in that area
                        measures by it — a room of hand-typed tasks accepts
                        the number, prices identically, and used to give the
                        estimator no way to tell.

                        Only once a figure is actually typed: an empty box
                        has nothing to warn about, and a warning sitting on
                        every untouched area would be noise on the way to
                        being ignored.
                      */
                                      hint={squareFeetHint(room, t)}
                                      min={0}
                                      max={MAX_MEASURED_SQUARE_FEET}
                                      value={room.measuredSquareFeet}
                                      disabled={readOnly}
                                      onValueChange={(squareFeet) =>
                                        patchRoom(index, {
                                          measuredSquareFeet:
                                            squareFeet === null ? null : Math.max(0, Math.trunc(squareFeet)),
                                        })
                                      }
                                    />
                                    <Select
                                      label={t('roomsStep.floorTypeLabel')}
                                      value={room.floorType ?? ''}
                                      disabled={readOnly}
                                      hint={t('roomsStep.floorTypeHint')}
                                      onChange={(event) =>
                                        patchRoom(index, {
                                          floorType: (event.target.value || null) as QuoteRoom['floorType'],
                                        })
                                      }
                                      options={[
                                        { value: '', label: t('roomsStep.floorTypeUnset') },
                                        ...FLOOR_TYPES.map((floorType) => ({
                                          value: floorType,
                                          label: t(`roomsStep.floorType.${floorType}`),
                                        })),
                                      ]}
                                    />
                                  </>
                                ) : null}
                              </div>

                              <div id={`quote-tasks-${room.id}`} tabIndex={-1} className="scroll-mt-4 outline-none">
                                <RoomTasks
                                  quote={quote}
                                  room={room}
                                  readOnly={readOnly}
                                  segments={segmentsForQuote(quote.segment, showGroups)}
                                  onChange={(next) => patchRoom(index, next)}
                                />
                              </div>

                              <details className="mt-3">
                                <summary className="min-h-11 cursor-pointer text-sm font-semibold">
                                  {t('servicesStep.notesHeading', { room: roomLabel(room) })}
                                </summary>
                                <TextArea
                                  label={t('servicesStep.customerNote')}
                                  rows={2}
                                  value={room.customerNotes ?? ''}
                                  disabled={readOnly}
                                  onChange={(event) => patchRoom(index, { customerNotes: event.target.value || null })}
                                />
                                <TextArea
                                  label={t('servicesStep.internalNote')}
                                  hint={t('servicesStep.internalNoteHint')}
                                  tone="internal"
                                  rows={2}
                                  value={room.internalNotes ?? ''}
                                  disabled={readOnly}
                                  onChange={(event) => patchRoom(index, { internalNotes: event.target.value || null })}
                                />
                              </details>

                              {!readOnly ? (
                                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[color:var(--color-line)] pt-3">
                                  {roomQuantity(room) > 1 ? (
                                    <Button
                                      onClick={() => splitOne(index)}
                                      disabled={quote.rooms.length >= MAX_QUOTE_ROOMS}
                                      aria-label={t('roomsStep.splitOneRoom', { room: roomLabel(room) })}
                                    >
                                      {t('roomsStep.splitOne')}
                                    </Button>
                                  ) : null}
                                  <Button onClick={() => duplicate(index)}>{t('roomsStep.duplicate')}</Button>
                                  <Button
                                    onClick={() => move(index, -1)}
                                    disabled={!canMove(index, -1)}
                                    aria-label={t('roomsStep.moveUp', { room: roomLabel(room) })}
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    onClick={() => move(index, 1)}
                                    disabled={!canMove(index, 1)}
                                    aria-label={t('roomsStep.moveDown', { room: roomLabel(room) })}
                                  >
                                    ↓
                                  </Button>
                                </div>
                              ) : null}
                              {!readOnly && roomQuantity(room) > 1 && quote.rooms.length >= MAX_QUOTE_ROOMS ? (
                                <p className="field-hint mt-2">{t('roomsStep.splitFull', { max: MAX_QUOTE_ROOMS })}</p>
                              ) : null}
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );

  return (
    <div>
      {/* A common job is a way to start: once rooms are in, it waits with
          the rest of the ways to add them. */}
      {listFirst ? null : <CommercialPresets quote={quote} update={update} readOnly={readOnly} />}
      <FirstVisitTasksToggle quote={quote} update={update} readOnly={readOnly} />
      {listFirst ? (
        <>
          {roomList}
          {addOpen ? (
            <div className="mt-6">
              <CommercialPresets quote={quote} update={update} readOnly={readOnly} />
            </div>
          ) : null}
          {addPanel}
        </>
      ) : (
        <>
          {addPanel}
          {roomList}
        </>
      )}
    </div>
  );
}
